import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectorRef } from "@angular/core";
import { Router } from "@angular/router";
import { ISystemLogDetailModel, ISystemLogModel, SystemLogModel, SystemLogDetailModel, SystemLogType, LSICalculationRequestModel, StructureTypeModel, StructureModel, OrendaChemicalHistoryModel, DosageRecommendationRequestModel, DosageRecommendationResponse, IDropdownModel, DropdownModel, IEquipmentTrackingModel, IDocumentModel, EquipmentTrackingModel, IAutoCoverSpecModel, AutoCoverSpecModel, AUTO_COVER_EQUIPMENT_NAME, ILathamOrderGridModel, IAutoCoverPreviousCoverModel, autoCoverMeasurementWarning, autoCoverTotalInches } from "@models";
import { StructureTypeService, OrendaService, GlobalsService, EquipmentTrackingService, DocumentsService, UtilsService, SleepService, AutoCoverToLathamOrderService, CustomersService, LathamOrdersService } from "@services";
import Swal from 'sweetalert2';
import * as moment from "moment";
import { SlickConfirmDialogComponent, SlickConfirmDialogResults, SlickDialogComponent } from "@slick-components";

@Component({
    selector: "equipment-tracking",
    templateUrl: "./equipment-tracking.component.html",
    styleUrls: ['equipment-tracking.component.scss'],
    providers: [EquipmentTrackingService, DocumentsService, CustomersService, LathamOrdersService]
})

export class EquipmentTrackingComponent implements OnChanges, OnInit {
    @Input() jobId!: number;
    @Input() jobDocumentKey!: string;
    @Input() isMobile: boolean;
    @Input() equipmentTrackingModels: IEquipmentTrackingModel[];
    @Input() showSaveButton: boolean = false;
    // Set by the Assets tab to scope this list to one pool/spa. Left null everywhere else, which keeps
    // the old job-wide behaviour. Saving with the wrong scope deletes the rows outside it, so this has
    // to be the same structureId the list was loaded with.
    @Input() structureId: number = null;

    @ViewChild("deleteEquipmentConfirmRef") deleteEquipmentConfirmRef: SlickConfirmDialogComponent;
    @ViewChild("duplicateFoundRef") duplicateFoundRef: SlickConfirmDialogComponent;
    @ViewChild("newNameDialogRef") newNameDialogRef: SlickDialogComponent;
    @ViewChild("editNameDialogRef") editNameDialogRef: SlickDialogComponent;
    @ViewChild("lathamOrderDialogRef") lathamOrderDialogRef: SlickDialogComponent;


    canDelete: boolean = GlobalsService.checkPermission('EquipmentTracking', 'Delete')
    canDeletePhotos: boolean = GlobalsService.checkPermission('EquipmentTracking', 'DeletePhotos')

    equipmentTrackingSpinnerStatus: string = "";   

    newName: string = '';
    editingEquipment: IEquipmentTrackingModel;
    dialogWidth: number = 400;

    filterTypes: IDropdownModel[] = [new DropdownModel("Cartridge", "Cartridge"),
        new DropdownModel("Sand", "Sand")];

    heaterTypes: IDropdownModel[] = [new DropdownModel("Electric", "Electric"),
        new DropdownModel("Gas", "Gas"),
        new DropdownModel("Heat Pump", "Heat Pump"),
    new DropdownModel("Solar", "Solar")]

    // Auto Cover carries a spec sheet instead of the generic manufacturer/model fields, and its
    // guided capture flow takes 7 photos, so it gets a bigger photo allowance than everything else.
    readonly autoCoverName: string = AUTO_COVER_EQUIPMENT_NAME;
    readonly autoCoverMaxPhotos: number = 8;
    readonly defaultMaxPhotos: number = 3;

    // Auto Cover width and length as they were loaded, per record, so the check against the covers
    // already ordered only questions measurements someone has just changed
    private loadedMeasurements: Record<number, string> = {};
    // Fetched the first time a changed measurement needs checking, once per job
    private previousCovers: IAutoCoverPreviousCoverModel[] = null;
    private previousCoversJobId: number = null;
    // The Auto Cover sheets as they were loaded or last saved, so leaving with edits still on them can
    // be caught rather than losing a long form somebody filled in and never saved
    private savedAutoCovers: string = "";

    constructor(private documentsService: DocumentsService,
        private equipmentTrackingService: EquipmentTrackingService,
        private customersService: CustomersService,
        private lathamOrdersService: LathamOrdersService,
        private autoCoverToLathamOrderService: AutoCoverToLathamOrderService,
        private router: Router) { }


    async ngOnChanges() {
        //this.equipmentTrackingModels = await this.equipmentTrackingService.getEquipmentTrackings(this.jobId);
        this.ensureAutoCoverSpecs();
        this.rememberLoadedMeasurements();
        this.savedAutoCovers = this.autoCoverSnapshot();
    }

    isAutoCover(equipmentTrackingModel: IEquipmentTrackingModel): boolean {
        return (equipmentTrackingModel?.equipmentName || "").trim().toLowerCase() === AUTO_COVER_EQUIPMENT_NAME.toLowerCase();
    }

    maxPhotos(equipmentTrackingModel: IEquipmentTrackingModel): number {
        return this.isAutoCover(equipmentTrackingModel) ? this.autoCoverMaxPhotos : this.defaultMaxPhotos;
    }

    // The server sends a spec sheet down with every Auto Cover record, but a record added client side
    // (or an older row saved before this existed) won't have one to bind to.
    private ensureAutoCoverSpecs() {
        (this.equipmentTrackingModels || [])
            .filter(x => this.isAutoCover(x) && !x.autoCover)
            .forEach(x => {
                const spec = new AutoCoverSpecModel();
                spec.companyId = GlobalsService.company.companyId;
                spec.equipmentTrackingId = x.equipmentTrackingId;
                x.autoCover = spec;
            });
    }

    // Desktop renders the whole spec sheet inline, so the section collapses (closed on load) to keep
    // the rest of the equipment list reachable. Phones only show the wizard launcher, which is already
    // one button, so there's nothing to collapse there.
    toggleAutoCover(equipmentTrackingModel: IEquipmentTrackingModel) {
        if (this.isMobile)
            return;

        equipmentTrackingModel.showAutoCover = !equipmentTrackingModel.showAutoCover;
    }

    // On a phone the spec sheet is captured through the guided wizard (spec steps, then one prompt per
    // photo) rather than inline in this accordion.
    openAutoCoverWizard() {
        // structureId tells the wizard which pool's cover it's editing. Omitted when this list isn't
        // scoped to one, which leaves the wizard on its old "the job's Auto Cover record" behaviour.
        const queryParams: any = { jobDocumentKey: this.jobDocumentKey };

        if (this.structureId > 0)
            queryParams.structureId = this.structureId;

        this.router.navigate(['/auto-cover', this.jobId], { queryParams });
    }

    // Save what the tech recorded, then hand the specs to the Latham Orders page, which opens a new
    // quote pre-filled from them. Saving first means the office is quoting off what's stored, not off
    // unsaved edits sitting in this tab.
    async quoteReplacementCover(equipmentTrackingModel: IEquipmentTrackingModel) {
        if (!this.isAutoCover(equipmentTrackingModel))
            return;

        // Backing out of the measurement check backs out of the quote too
        if (!(await this.saveEquipment()))
            return;

        const saved = (this.equipmentTrackingModels || []).find(x => this.isAutoCover(x));
        const spec: IAutoCoverSpecModel = saved?.autoCover ?? equipmentTrackingModel.autoCover;

        // The pool is at the job site, so the job's customer is the order's home owner. Fetched here
        // rather than passed in, because the four places this component is hosted don't all have the
        // job's customer to hand.
        const homeOwner = await this.customersService.getCustomerByJobId(this.jobId);

        this.autoCoverToLathamOrderService.setPendingRequest({
            jobId: this.jobId,
            equipmentTrackingId: saved?.equipmentTrackingId ?? 0,
            spec: spec,
            homeOwner: homeOwner
        });

        this.router.navigate(['/lathamOrders']);
    }

    fillFromOrderSpinnerStatus: string = "";

    // ---- Fill From Latham Order ----
    // The reverse of the button above: pull the specs off a Latham order this company already built
    // and drop them onto the sheet. Faster and more accurate than reading a cover the office has the
    // paperwork for, and it links the sheet to that order.
    //
    // Picked from a searchable list (same shape as Link Existing Invoice on the job) rather than typed
    // in as a number, so the office can find the order by the customer's name or PO when they don't
    // have the number to hand.
    lathamOrderSearchString: string = "";
    // The list endpoint takes one status at a time. Submitted is the default because a cover that
    // exists was built from a submitted order; quotes are there for one that hasn't shipped yet.
    showLathamQuotes: boolean = false;
    lathamOrders: ILathamOrderGridModel[] = [];
    isLoadingLathamOrders: boolean = false;
    lathamOrdersHasMore: boolean = false;
    private lathamOrdersPage: number = 1;
    private readonly lathamOrdersPageSize: number = 10;
    // The sheet the picked order gets applied to, held while the dialog is open.
    private fillTargetEquipment: IEquipmentTrackingModel;
    // Which row is being fetched, so only that row's button spins rather than all of them.
    selectingLathamOrderId: string = null;

    get lathamOrderDialogWidth(): number {
        return this.isMobile ? 320 : 600;
    }

    async fillFromLathamOrder(equipmentTrackingModel: IEquipmentTrackingModel) {
        if (!this.isAutoCover(equipmentTrackingModel))
            return;

        this.fillTargetEquipment = equipmentTrackingModel;
        this.lathamOrderSearchString = "";
        this.showLathamQuotes = false;
        this.lathamOrders = [];
        this.lathamOrdersPage = 1;
        this.fillFromOrderSpinnerStatus = "reset";

        this.lathamOrderDialogRef.showDialog();

        await this.loadLathamOrders(true);
    }

    async refreshLathamOrders() {
        // Lets the keystroke land on the model before the search runs, matching how the invoice picker
        // handles its search box.
        await SleepService.sleep();

        this.lathamOrdersPage = 1;
        await this.loadLathamOrders(true);
    }

    async loadMoreLathamOrders() {
        this.lathamOrdersPage++;
        await this.loadLathamOrders(false);
    }

    private async loadLathamOrders(reset: boolean) {
        this.isLoadingLathamOrders = true;

        try {
            const orderStatus = this.showLathamQuotes ? "Quote" : "Submitted";
            const page = await this.lathamOrdersService.getLathamOrdersForInfiniteScroll(
                orderStatus, this.lathamOrdersPage, (this.lathamOrderSearchString ?? "").trim());

            const rows = page ?? [];
            this.lathamOrders = reset ? rows : [...this.lathamOrders, ...rows];

            // The endpoint pages at a fixed size, so a short page is the end of the list.
            this.lathamOrdersHasMore = rows.length >= this.lathamOrdersPageSize;
        }
        catch {
            if (reset)
                this.lathamOrders = [];

            this.lathamOrdersHasMore = false;
        }
        finally {
            this.isLoadingLathamOrders = false;
        }
    }

    // The list rows are grid records and don't carry the spec sheet, so the full order is fetched by
    // its order number - the same lookup the old "type the number" prompt used.
    async selectLathamOrder(lathamOrder: ILathamOrderGridModel) {
        const orderId = (lathamOrder?.orderId ?? "").trim();
        if (!orderId || !this.fillTargetEquipment)
            return;

        try {
            this.selectingLathamOrderId = orderId;
            this.fillFromOrderSpinnerStatus = "spin";

            const lathamOrderModel = await this.lathamOrdersService.getLathamOrderByOrderId(orderId);

            if (!lathamOrderModel?.lathamOrderId) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Order not found',
                    text: `No Latham order ${orderId} was found.`,
                    confirmButtonColor: '#007bff',
                    heightAuto: false
                });
                return;
            }

            // Replaced rather than mutated so the spec sheet re-runs ngOnChanges and rebuilds the
            // option lists that hang off the manufacturer.
            this.fillTargetEquipment.autoCover = this.autoCoverToLathamOrderService
                .applyOrderToSpecs(this.fillTargetEquipment.autoCover, lathamOrderModel);

            // Opened here because the values only mean anything once they're on screen to check.
            this.fillTargetEquipment.showAutoCover = true;

            this.lathamOrderDialogRef.hideDialog();
        }
        catch (err: any) {
            await Swal.fire({
                icon: 'error',
                title: 'Unable to load the order',
                text: err?.error || err?.message || `Latham order ${orderId} couldn't be loaded.`,
                confirmButtonColor: '#007bff',
                heightAuto: false
            });
        }
        finally {
            this.fillFromOrderSpinnerStatus = "reset";
            this.selectingLathamOrderId = null;
        }
    }

    cancelLathamOrderPicker() {
        this.lathamOrderDialogRef.hideDialog();
    }

    ngOnInit() {
        if(this.isMobile)
        this.dialogWidth = 300
    }

    showAddNewDialog() {
        this.newName = '';
        this.newNameDialogRef.showDialog();
    }

    cancelAdd() {
        this.newName = '';
        this.newNameDialogRef.hideDialog();
    }

    editName(equipment: IEquipmentTrackingModel) {
        this.newName = equipment.equipmentName;
        this.editingEquipment = equipment;
        this.editNameDialogRef.showDialog();

    }

    onEditNameOk() {
        if (this.checkIfNameExists()) {
            this.duplicateFoundRef.confirm();
            return;
        }
        this.editingEquipment.equipmentName = this.newName
        this.editNameDialogRef.hideDialog();
    }

    cancelEdit() {
        this.newName = '';
        this.editNameDialogRef.hideDialog();
    }

    checkIfNameExists() {
        return this.equipmentTrackingModels.find(x => x.equipmentName === this.newName);
    }

    addEquipment() {
        if (this.checkIfNameExists()) {
            this.duplicateFoundRef.confirm();
            return;
        }
        this.newNameDialogRef.hideDialog();
        let newEquipmentTrackingModel = new EquipmentTrackingModel();
        newEquipmentTrackingModel.isDefault = false;
        newEquipmentTrackingModel.companyId = GlobalsService.company.companyId;
        newEquipmentTrackingModel.jobId = this.jobId;
        newEquipmentTrackingModel.structureId = this.structureId;
        newEquipmentTrackingModel.showNotes = false;
        newEquipmentTrackingModel.showDropdown = false;
        newEquipmentTrackingModel.equipmentName = this.newName;

        this.equipmentTrackingModels.push(newEquipmentTrackingModel);
    }

    // False only when the user backs out at the measurement check. A failed save still shows on the
    // spinner and lets the caller carry on, as it always has.
    async saveEquipment(): Promise<boolean> {
        return (await this.trySaveEquipment()) !== "cancelled";
    }

    // ---- unsaved Auto Cover changes ----

    // Only the spec sheet is watched: it's the long form that gets lost, and its fields change only when
    // somebody edits them, so this never claims there are changes when there aren't
    hasUnsavedAutoCoverChanges(): boolean {
        return this.autoCoverSnapshot() !== this.savedAutoCovers;
    }

    // For a host leaving this list, eg the job's own Save. True when there was nothing to save or it
    // saved; false when the user backed out at the measurement check or the save failed, so the caller
    // can stay put with the edits still on screen.
    async saveIfAutoCoverChanged(): Promise<boolean> {
        if (!this.hasUnsavedAutoCoverChanges())
            return true;

        return (await this.trySaveEquipment()) === "saved";
    }

    private autoCoverSnapshot(): string {
        return JSON.stringify((this.equipmentTrackingModels || [])
            .filter(x => this.isAutoCover(x))
            .map(x => this.recordedAutoCoverValues(x.autoCover)));
    }

    // What's filled in, with empty and missing treated alike and dates compared as days, so a value that
    // only changed shape (a date string loaded from the server vs the picker's Date) doesn't count
    private recordedAutoCoverValues(spec: IAutoCoverSpecModel): Record<string, string> {
        const bookkeeping = ["equipmentTrackingAutoCoverId", "companyId", "equipmentTrackingId", "lathamOrderId"];
        const values: Record<string, string> = {};

        Object.keys(spec ?? {})
            .filter(key => bookkeeping.indexOf(key) < 0)
            .sort()
            .forEach(key => {
                const value = spec[key];
                if (value === null || value === undefined || value === "")
                    return;

                values[key] = (value instanceof Date || /^\d{4}-\d{2}-\d{2}T/.test(value))
                    ? moment(value).format("YYYY-MM-DD")
                    : String(value);
            });

        return values;
    }

    private async trySaveEquipment(): Promise<"saved" | "cancelled" | "failed"> {
        if (!(await this.confirmAutoCoverMeasurements()))
            return "cancelled";

        try {
            this.equipmentTrackingSpinnerStatus = "spin";

            // doing a sleep here just to make the spin actually show
            await SleepService.sleep(200);

            let equipmentTrackingsToUpdate = this.equipmentTrackingModels.filter(x =>
                // A saved Auto Cover always goes, even emptied out: one missing from the list is deleted,
                // and the sheet has no delete button, so clearing it shouldn't take the record and its
                // photos with it
                (this.isAutoCover(x) && x.equipmentTrackingId > 0) ||
                (x.equipmentName && x.equipmentName.trim() && !x.isDefault) ||
                (x.manufacturer && x.manufacturer.trim()) ||
                (x.model && x.model.trim()) ||
                (x.oemsku && x.oemsku.trim()) ||
                (x.serialNumber && x.serialNumber.trim()) ||
                x.installedDate ||
                x.filterType ||
                x.btu ||
                x.heaterType ||
                (x.documents && x.documents.length > 0) ||
                // Auto Cover records hold everything in the spec sheet, so none of the generic fields
                // above ever fill in for them
                this.hasAutoCoverSpecs(x)
            );

            equipmentTrackingsToUpdate.forEach(x => {
                if (x.manufacturer === "Other") {
                    x.manufacturer = x.otherManufacturer;
                    x.otherManufacturer = null;
                }
            })
            
            await this.equipmentTrackingService.saveEquipmentTrackings(this.jobId, equipmentTrackingsToUpdate, this.jobDocumentKey, this.structureId);
            this.equipmentTrackingModels = await this.equipmentTrackingService.getEquipmentTrackings(this.jobId, this.structureId);
            this.ensureAutoCoverSpecs();
            this.rememberLoadedMeasurements();
            this.savedAutoCovers = this.autoCoverSnapshot();
            this.equipmentTrackingSpinnerStatus = "ok";

        }
        catch {
            this.equipmentTrackingSpinnerStatus = "error";
            return "failed";
        }

        return "saved";
    }

    // ---- check new measurements against the covers already ordered ----

    private measurementKey(spec: IAutoCoverSpecModel): string {
        return `${autoCoverTotalInches(spec?.widthFeet, spec?.widthInches)}|${autoCoverTotalInches(spec?.lengthFeet, spec?.lengthInches)}`;
    }

    private rememberLoadedMeasurements() {
        this.loadedMeasurements = {};

        (this.equipmentTrackingModels || [])
            .filter(x => this.isAutoCover(x))
            .forEach(x => this.loadedMeasurements[x.equipmentTrackingId ?? 0] = this.measurementKey(x.autoCover));
    }

    // Asks before saving a width or length that matches none of the covers this company has ordered for
    // the customer. Only changed measurements are checked, and a lookup that fails never holds up a save.
    private async confirmAutoCoverMeasurements(): Promise<boolean> {
        if (!this.jobId)
            return true;

        const changed = (this.equipmentTrackingModels || [])
            .filter(x => this.isAutoCover(x) && this.hasAutoCoverSpecs(x))
            .filter(x => this.measurementKey(x.autoCover) !== this.loadedMeasurements[x.equipmentTrackingId ?? 0]);

        if (changed.length === 0)
            return true;

        if (this.previousCoversJobId !== this.jobId) {
            try {
                this.previousCovers = await this.equipmentTrackingService.getPreviousCovers(this.jobId);
            }
            catch {
                this.previousCovers = [];
            }

            this.previousCoversJobId = this.jobId;
        }

        for (const equipment of changed) {
            const warning = autoCoverMeasurementWarning(equipment.autoCover, this.previousCovers);
            if (!warning)
                continue;

            const result = await Swal.fire({
                icon: 'warning',
                title: 'Check the measurements',
                text: warning,
                showCancelButton: true,
                confirmButtonText: 'Save Anyway',
                cancelButtonText: 'Go Back',
                confirmButtonColor: '#007bff',
                heightAuto: false
            });

            if (!result.isConfirmed)
                return false;
        }

        return true;
    }

    // True once a tech has recorded anything at all on the spec sheet.
    hasAutoCoverSpecs(equipmentTrackingModel: IEquipmentTrackingModel): boolean {
        const spec = equipmentTrackingModel?.autoCover;
        if (!spec || !this.isAutoCover(equipmentTrackingModel))
            return false;

        // Keys and back-references aren't something the tech recorded
        const bookkeeping = ["equipmentTrackingAutoCoverId", "companyId", "equipmentTrackingId", "lathamOrderId"];

        return Object.keys(spec)
            .filter(key => bookkeeping.indexOf(key) < 0)
            .some(key => {
                const value = spec[key];
                return value !== null && value !== undefined && value !== "";
            });
    }

    async deleteEquipment(idx: number) {
        const result = await this.deleteEquipmentConfirmRef.confirm();

        if (result === SlickConfirmDialogResults.Ok) {
            this.equipmentTrackingModels.splice(idx, 1);
        }
    }

    async onDocumentClicked(documentModel: IDocumentModel) {
        this.documentsService.openDocument(documentModel.documentId);
    }

    async onFileChanged(fileModel: IDocumentModel) {
        await this.documentsService.updateDocument(fileModel);
    }

    async onFileDeleted(fileModel: IDocumentModel) {
        await this.documentsService.deleteDocument(fileModel.documentId);
    }

    async onNewFilesAdded(files: IDocumentModel[], idx: number) {
        let equipmentTrackingModel = this.equipmentTrackingModels[idx];
        
        const maxFiles = this.maxPhotos(equipmentTrackingModel);

        if (((equipmentTrackingModel.documents?.length ?? 0) + files.length) > maxFiles) {
            await Swal.fire({
                icon: 'warning',
                title: 'Oops',
                text: `You can only upload up to ${maxFiles} files. Please reduce your selection and try again.`,
                confirmButtonColor: '#007bff',
                width: '28em',
                heightAuto: false
            });
            return;
        }
        if (!equipmentTrackingModel.equipmentTrackingId || equipmentTrackingModel.equipmentTrackingId === 0) {
            const autoCoverSpec = equipmentTrackingModel.autoCover;
            equipmentTrackingModel = await this.equipmentTrackingService.addEquipmentTrackingByPhotoUpload(equipmentTrackingModel, this.jobDocumentKey);
            // The photo-upload endpoint only creates the equipment row, so carry the in-progress spec
            // sheet across rather than losing what the tech has typed so far.
            equipmentTrackingModel.autoCover = equipmentTrackingModel.autoCover ?? autoCoverSpec;
        }

        const newDocuments = await this.documentsService.addDocuments(equipmentTrackingModel.documentKey, files);
        equipmentTrackingModel.documents = [...equipmentTrackingModel.documents, ...newDocuments];
        this.equipmentTrackingModels[idx] = equipmentTrackingModel;
        this.equipmentTrackingModels = this.equipmentTrackingService.applyOrendaConfigIfNeeded(this.equipmentTrackingModels)

    }

    onManufacturerChanged(manufacturerDropdown: IDropdownModel, model: IEquipmentTrackingModel) {
        model.manufacturer = manufacturerDropdown.id;
        model.model = null;

        if (model.manufacturer === "Other") {
            model.showDropdown = false;
        }
        else
            model.showDropdown = true;

        const config = this.equipmentTrackingService.equipmentConfig[model.equipmentName];
        if (!config) {
            model.modelOptions = [];
            return;
        }

        model.modelOptions =
            config.modelsByManufacturer[model.manufacturer] ?? [];
    }

}